import React, { useState } from 'react';
import { X, PackageX, Printer, AlertCircle, CheckCircle } from 'lucide-react';
import { stcTransService } from '../../../../../services/stcTransService';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface CloseSortingBoxModalProps {
    isOpen: boolean;
    onClose: () => void;
    batchId: string;
    stcBoxNo: number;
    stcBoxTypeID: string;
    description: string;
    linesInBox: number;
    onBoxClosed: (printNewLabel: boolean) => void;
}

export const CloseSortingBoxModal: React.FC<CloseSortingBoxModalProps> = ({
    isOpen,
    onClose,
    batchId,
    stcBoxNo,
    stcBoxTypeID,
    description,
    linesInBox,
    onBoxClosed
}) => {
    const { t } = useTranslation();
    const [isClosing, setIsClosing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    if (!isOpen) return null;

    const handleCloseBox = async (printNewLabel: boolean) => {
        setIsClosing(true);
        setError(null);

        try {
            const result = await stcTransService.closeSortingBox(batchId, stcBoxNo);

            if (result.success) {
                setSuccess(true);
                setTimeout(() => {
                    onBoxClosed(printNewLabel);
                }, 1000);
            } else {
                setError(result.errorMessage || t('warehouse.modules.receipt.sortingBoxClose.unknownError'));
            }
        } catch (err: any) {
            setError(err.message || t('warehouse.modules.receipt.sortingBoxClose.closeError'));
        } finally {
            setIsClosing(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 bg-orange-500 text-white rounded-t-xl">
                    <div className="flex items-center gap-2">
                        <PackageX className="h-5 w-5" />
                        <h2 className="text-lg font-semibold">
                            {t('warehouse.modules.receipt.closeSortingBoxTitle')}
                        </h2>
                    </div>
                    <button onClick={onClose} className="hover:bg-orange-600 rounded p-1 transition-colors">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="px-6 py-5 space-y-4">
                    {/* Box info */}
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                        <div className="text-sm text-orange-700 font-medium mb-1">
                            {t('warehouse.modules.receipt.sortingBoxZone', { zone: description })}
                        </div>
                        <div className="text-2xl font-bold text-orange-800">
                            #{stcBoxNo}
                        </div>
                        <div className="text-sm text-orange-600 mt-1">
                            {t('warehouse.modules.receipt.linesInBox', { count: linesInBox })}
                        </div>
                    </div>

                    {/* Error */}
                    {error && (
                        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                            <AlertCircle className="h-4 w-4 flex-shrink-0" />
                            <span className="text-sm">{error}</span>
                        </div>
                    )}

                    {/* Success */}
                    {success && (
                        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">
                            <CheckCircle className="h-4 w-4 flex-shrink-0" />
                            <span className="text-sm">
                                {t('warehouse.modules.receipt.sortingBoxClosed', { boxNo: stcBoxNo, count: linesInBox })}
                            </span>
                        </div>
                    )}
                </div>

                {/* Actions */}
                {!success && (
                    <div className="px-6 pb-5 space-y-3">
                        <button
                            onClick={() => handleCloseBox(false)}
                            disabled={isClosing || linesInBox === 0}
                            className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors ${
                                isClosing || linesInBox === 0
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-orange-500 text-white hover:bg-orange-600'
                            }`}
                        >
                            <PackageX className="h-4 w-4" />
                            {isClosing ? t('warehouse.modules.receipt.sortingBoxClose.closing') : t('warehouse.modules.receipt.closeSortingBox')}
                        </button>

                        <button
                            onClick={() => handleCloseBox(true)}
                            disabled={isClosing || linesInBox === 0}
                            className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors ${
                                isClosing || linesInBox === 0
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-green-500 text-white hover:bg-green-600'
                            }`}
                        >
                            <Printer className="h-4 w-4" />
                            {t('warehouse.modules.receipt.closeSortingBox')} + {t('warehouse.modules.receipt.printNewLabel')}
                        </button>

                        <button
                            onClick={onClose}
                            className="w-full px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm"
                        >
                            {t('common.buttons.cancel', {}, 'Avbryt')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
